/**
 * CRM 견적 API 의 HTTP 계층.
 *
 * 브라우저가 CRM 을 직접 부른다. CRM CORS 가 호출 오리진만 허용하고, 레이트리밋
 * (IP 3/분)도 프록시 IP 가 아니라 실제 사용자 IP 로 걸려야 하기 때문이다. 메일에
 * 실리는 견적서 링크도 CRM 이 이 요청의 Origin 으로 만든다.
 *
 * 이 파일에는 React 도 react-query 도 없다 — 앱마다 react-query 메이저가 달라
 * (quiz v5 / web v4) 훅은 공유할 수 없고, 공유해서 이득인 건 HTTP 계약뿐이다.
 * 호출부는 이 인터페이스에 의존한다 — 스토리·테스트는 `window.fetch` 를 갈아끼우지
 * 말고 가짜 클라이언트를 주입한다.
 */

export interface CrmErrorBody {
  error?: {
    code?: string;
    message?: string;
    /** 서버가 지목한 입력 필드 → 메시지 */
    fields?: Record<string, string | undefined>;
    raw?: unknown;
  };
}

export class CrmApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: CrmErrorBody,
    fallbackMessage: string,
  ) {
    super(body.error?.message || fallbackMessage);
    this.name = 'CrmApiError';
  }
}

export interface CrmClient {
  /** 성공 시 파싱된 응답 본문. 실패면 CrmApiError 를 throw 한다. */
  post(path: string, body: unknown): Promise<unknown>;
  get(path: string): Promise<unknown>;
}

export interface CrmClientOptions {
  /** 사용자에게 보일 문구는 도메인마다 달라 호출부가 정한다. */
  messages: {
    /** 호출 주소를 만들 수 없을 때 */
    notConfigured: string;
    /** 그 밖의 실패(서버가 메시지를 안 줬을 때의 기본값) */
    failed: string;
  };
  /**
   * 호출 시점에 base 주소를 정한다. 패키지는 앱의 env 이름을 알면 안 되므로
   * (`NEXT_PUBLIC_CRM_URL` 은 앱 사정이다) 소비 앱이 넘긴다. 로컬 프록시처럼
   * 절대 주소가 아닌 경로를 쓰는 환경은 `resolveUrl` 로 통째로 갈아끼운다.
   */
  resolveBaseUrl: () => string | undefined;
  /** base+path 조합 자체를 대체한다(로컬 rewrite 프록시 등). */
  resolveUrl?: (path: string) => string;
  fetchImpl?: typeof fetch;
}

/** base 주소에 경로를 붙인다. 주소가 없으면 부르지 않는다. */
function joinUrl(
  path: string,
  rawBase: string | undefined,
  notConfiguredMessage: string,
): string {
  const base = rawBase?.trim();
  // 주소가 없으면 부르지 않는다 — 다른 환경으로 조용히 새지 않게.
  if (!base) {
    throw new CrmApiError(
      0,
      { error: { code: 'crm_not_configured' } },
      notConfiguredMessage,
    );
  }
  // base 에 경로(/api)가 붙은 환경도 오리진 기준으로 맞춘다.
  return new URL(path, base).toString();
}

async function parseBody(response: Response, fallbackMessage: string) {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return {
      error: {
        code: 'invalid_json_response',
        message: fallbackMessage,
        raw: text,
      },
    } satisfies CrmErrorBody;
  }
}

export function createCrmClient({
  messages,
  resolveBaseUrl,
  resolveUrl,
  fetchImpl,
}: CrmClientOptions): CrmClient {
  async function request(path: string, init: RequestInit): Promise<unknown> {
    const url =
      resolveUrl?.(path) ??
      joinUrl(path, resolveBaseUrl(), messages.notConfigured);
    const doFetch = fetchImpl ?? fetch;
    const response = await doFetch(url, init);

    const parsed = await parseBody(response, messages.failed);
    if (!response.ok) {
      throw new CrmApiError(
        response.status,
        parsed as CrmErrorBody,
        messages.failed,
      );
    }

    return parsed;
  }

  return {
    post(path, body) {
      return request(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    },
    get(path) {
      return request(path, { method: 'GET' });
    },
  };
}
